import React, { useEffect, useState } from 'react';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';

type Geo = {
  country?: string | null;
  countryCode?: string | null;
  city?: string | null;
  lat?: number | null;
  lon?: number | null;
  isLocal?: boolean;
};

type Precise = { lat: number; lon: number; accuracy?: number | null };

type OnlineAdmin = {
  id: string;
  email: string;
  name: string;
  role: string;
  ip?: string | null;
  geo?: Geo | null;
  precise?: Precise | null;
  lastSeen: string;
  lastSeenSecondsAgo: number;
  onlineSince: string;
};

// ISO 3166-1 alpha-2 -> flag emoji.
function flag(code?: string | null): string {
  if (!code || code.length !== 2 || !/^[A-Za-z]{2}$/.test(code)) return '🌐';
  const cc = code.toUpperCase();
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function locationText(geo?: Geo | null): string {
  if (!geo) return 'Locating…';
  if (geo.isLocal) return 'Local network';
  const parts = [geo.city, geo.country].filter(Boolean);
  return parts.length ? parts.join(', ') : 'Unknown';
}

function mapLink(geo?: Geo | null): string | null {
  if (!geo || geo.lat == null || geo.lon == null) return null;
  return `https://www.openstreetmap.org/?mlat=${geo.lat}&mlon=${geo.lon}#map=10/${geo.lat}/${geo.lon}`;
}

function ago(seconds: number): string {
  if (seconds < 10) return 'now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

/**
 * Shows admins currently using the dashboard, with IP-based location + flag.
 * Sends a heartbeat on an interval so this admin appears in everyone's list,
 * and polls the online list.
 */
export const OnlineAdmins: React.FC = () => {
  const [admins, setAdmins] = useState<OnlineAdmin[]>([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;

    const beat = async () => {
      try {
        await apiClient.post(API_ENDPOINTS.ADMIN_PRESENCE_HEARTBEAT, {});
      } catch {
        /* ignore */
      }
    };
    const load = async () => {
      try {
        const res = await apiClient.get(API_ENDPOINTS.ADMIN_GET_ONLINE_ADMINS);
        if (!active) return;
        if (res.data?.status === 'success' && res.data?.data) {
          setAdmins(res.data.data.users || []);
          setError(false);
        }
      } catch {
        if (active) setError(true);
      }
    };

    // Share precise browser location (if the admin allows it) so their own row
    // shows an exact position rather than the coarse IP estimate. Best-effort;
    // browser location can't be forced.
    const shareLocation = () => {
      if (!('geolocation' in navigator)) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          apiClient
            .post(API_ENDPOINTS.ADMIN_PRESENCE_GEO, {
              lat: pos.coords.latitude,
              lon: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            })
            .catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000 },
      );
    };

    void beat();
    void load();
    shareLocation();
    const beatTimer = setInterval(beat, 20000);
    const loadTimer = setInterval(load, 15000);
    const geoTimer = setInterval(shareLocation, 120000);
    return () => {
      active = false;
      clearInterval(beatTimer);
      clearInterval(loadTimer);
      clearInterval(geoTimer);
    };
  }, []);

  return (
    <div
      style={{
        background: 'rgba(30, 41, 59, 0.6)',
        border: '1px solid rgba(100, 116, 139, 0.25)',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: admins.length > 0 ? '#22c55e' : '#64748b',
            boxShadow: admins.length > 0 ? '0 0 0 3px rgba(34,197,94,0.2)' : 'none',
          }}
        />
        <h3 style={{ margin: 0, fontSize: 16, color: '#F1F5F9' }}>Admins online</h3>
        <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 600, color: '#94A3B8' }}>{admins.length}</span>
      </div>

      {error ? (
        <p style={{ margin: 0, color: '#ef4444', fontSize: 13 }}>Could not load online admins.</p>
      ) : admins.length === 0 ? (
        <p style={{ margin: 0, color: '#94A3B8', fontSize: 13 }}>No other admins online.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {admins.map((a) => {
            const preciseLink = a.precise
              ? `https://www.openstreetmap.org/?mlat=${a.precise.lat}&mlon=${a.precise.lon}#map=15/${a.precise.lat}/${a.precise.lon}`
              : null;
            const link = mapLink(a.geo);
            const label = a.name || a.email || `admin ${a.id.slice(0, 8)}`;
            return (
              <li key={a.id} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: a.lastSeenSecondsAgo < 60 ? '#22c55e' : '#f59e0b',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: '#F1F5F9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                    {label}
                  </span>
                  {a.role && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#d4af37', border: '1px solid #d4af37', borderRadius: 6, padding: '1px 6px', flexShrink: 0 }}>
                      {a.role}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', color: '#94A3B8', fontSize: 12, flexShrink: 0 }}>{ago(a.lastSeenSecondsAgo)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 15, fontSize: 12.5, color: '#94A3B8' }}>
                  {preciseLink ? (
                    <a
                      href={preciseLink}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#22c55e', textDecoration: 'none' }}
                      title={`Exact location${a.precise?.accuracy ? ` (±${Math.round(a.precise.accuracy)}m)` : ''}`}
                    >
                      📍 Exact location ↗
                    </a>
                  ) : (
                    <>
                      <span>{flag(a.geo?.countryCode)}</span>
                      {link ? (
                        <a href={link} target="_blank" rel="noreferrer" style={{ color: '#d4af37', textDecoration: 'none' }}>
                          {locationText(a.geo)} ↗
                        </a>
                      ) : (
                        <span>{locationText(a.geo)}</span>
                      )}
                    </>
                  )}
                  {a.email && a.name && <span style={{ color: '#64748b' }}>{a.email}</span>}
                  {a.ip && <span style={{ color: '#64748b', fontFamily: 'monospace' }}>{a.ip}</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
